import React from 'react';
import Logo from 'components/shared/Logo/Logo';
import ThemedText from 'components/shared/ThemedText/ThemedText';
import UnlockedText from 'components/shared/UnlockedText/UnlockedText';
import YouTubeEmbed from 'components/shared/YouTubeEmbed/YouTubeEmbed';
import { EmbeddedNode } from 'components/shared/RichTextTable/types';
import { Node as ContentfulNode } from '@contentful/rich-text-types';
import { ContentfulRichTextBlock, DocumentWithEmbeddedEntries } from 'libs/contentful/types';
import Button from 'components/shared/Buttons/BaseButton';
import BoxTable from 'components/TextEditor/BoxTable';
import { getCurrentPageDisclosureNumberingMap } from 'utils/disclosures';
import RichTextInline from 'components/shared/RichTextInline/RichTextInline';
import { renderSuperscript } from 'utils/superscriptUtils';
import Linebreak from 'components/shared/Linebreak/Linebreak';
import BaseModal from '../shared/BaseModal/BaseModal';

// Map of disclosure sys.id to number for inline disclosure numbering
export type DisclosureNumberingMap = Map<string, number>;

const renderEmbeddedEntry = (
  node: ContentfulNode,
  children: React.ReactNode,
  content: DocumentWithEmbeddedEntries,
  disclosureNumberingMap?: DisclosureNumberingMap,
) => {
  const embeddedNode = node as EmbeddedNode;
  const inlineEntries = content?.links?.entries?.inline || [];
  const entry = inlineEntries.find(i => i.sys?.id === embeddedNode.data.target.sys.id);

  if (!entry) return null;

  // change: lower case this value to make it compatible with
  // experiments
  switch (entry.__typename) {
    case 'Logo':
      return <Logo image={entry.image!} __typename="Logo" />;
    case 'Cta':
      return (
        <span className="inline-block max-w-full" data-embedded-cta>
          <Button href={entry.url!} __typename="Button">
            {entry.text!}
          </Button>
        </span>
      );
    case 'ThemedText':
      return (
        <ThemedText themedText={entry.themedText as unknown as DocumentWithEmbeddedEntries} __typename="ThemedText" />
      );
    case 'UnlockedText':
      return (
        <UnlockedText
          unlockedText={entry.unlockedText as unknown as DocumentWithEmbeddedEntries}
          __typename="unlockedtext"
        />
      );
    case 'YouTubeEmbed':
      return <YouTubeEmbed youTubeVideoUrl={entry.youTubeVideoUrl!} __typename="YouTubeEmbed" />;
    case 'BoxTable':
      return (
        <BoxTable sys={entry.sys} tableHeader={entry.tableHeader!} tableBody={entry.tableBody!} __typename="BoxTable" />
      );
    case 'Disclosure':
      // For embedded disclosures, show the symbol or number as a superscript
      // Use provided map or fall back to current page map
      const numberingMap = disclosureNumberingMap || getCurrentPageDisclosureNumberingMap();
      const disclosureNumber = numberingMap?.get(entry.sys?.id || '');
      const symbol = entry?.symbol;
      const number = disclosureNumber?.toString();

      // Only render superscript if we have a valid symbol or number
      if (symbol || number) {
        return <span className="font-inherit">{renderSuperscript(symbol || number)}</span>;
      }

      // If no symbol or number, don't render anything
      return null;
    case 'Modal':
      return (
        <BaseModal
          title={entry.headline!}
          body={entry.copy!}
          openButtonText={entry.disclaimerText!}
          iconPosition={entry.iconPosition}
          iconOnlyLink={entry.iconOnlyLink}
        />
      );
    case 'Shortcode':
      return <RichTextInline inline={entry.shortCodeValue as ContentfulRichTextBlock} />;
    case 'Linebreak':
      return <Linebreak breakBehavior={entry.breakBehavior!} testId={entry.testId} />;
    default:
      return null;
  }
};

export default renderEmbeddedEntry;
